"use client";

import { useState } from "react";
import { Search, ZoomIn, ZoomOut } from "lucide-react";
import type { TimelineEra } from "@/lib/types";

interface TimelineControlsProps {
  eras: TimelineEra[];
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onEraJump: (eraId: string) => void;
  onSearch: (query: string) => void;
}

export default function TimelineControls({
  eras,
  zoom,
  onZoomChange,
  onEraJump,
  onSearch,
}: TimelineControlsProps) {
  const [query, setQuery] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (query.trim()) onSearch(query.trim());
  }

  return (
    <div className="absolute top-4 left-4 right-4 z-10 flex items-center gap-3 flex-wrap">
      {/* Search */}
      <div
        className="rounded-lg px-3 py-2"
        style={{
          background: "rgba(10, 14, 26, 0.85)",
          border: "1px solid rgba(200, 164, 94, 0.15)",
          backdropFilter: "blur(12px)",
        }}
      >
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Search size={14} style={{ color: "rgba(232, 224, 208, 0.5)" }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events..."
            className="bg-transparent text-sm outline-none w-40 placeholder:text-white/30"
            style={{ color: "#e8e0d0" }}
          />
        </form>
      </div>

      {/* Era Quick Jump */}
      <div
        className="rounded-lg px-2 py-1 flex items-center gap-1 overflow-x-auto"
        style={{
          background: "rgba(10, 14, 26, 0.85)",
          border: "1px solid rgba(200, 164, 94, 0.15)",
          backdropFilter: "blur(12px)",
        }}
      >
        {eras.map((era) => (
          <button
            key={era.id}
            onClick={() => onEraJump(era.id)}
            className="px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-all hover:brightness-125"
            style={{
              backgroundColor: `${era.color}22`,
              color: era.color,
              border: `1px solid ${era.color}33`,
            }}
          >
            {era.name}
          </button>
        ))}
      </div>

      {/* Zoom */}
      <div
        className="rounded-lg px-2 py-1 flex items-center gap-2 ml-auto"
        style={{
          background: "rgba(10, 14, 26, 0.85)",
          border: "1px solid rgba(200, 164, 94, 0.15)",
          backdropFilter: "blur(12px)",
        }}
      >
        <button
          onClick={() => onZoomChange(Math.max(0.5, zoom - 0.25))}
          className="p-1 rounded transition-colors hover:bg-white/10"
          style={{ color: "#e8e0d0" }}
        >
          <ZoomOut size={14} />
        </button>
        <input
          type="range"
          min={0.5}
          max={3}
          step={0.25}
          value={zoom}
          onChange={(e) => onZoomChange(parseFloat(e.target.value))}
          className="w-20 accent-[#c8a45e]"
        />
        <button
          onClick={() => onZoomChange(Math.min(3, zoom + 0.25))}
          className="p-1 rounded transition-colors hover:bg-white/10"
          style={{ color: "#e8e0d0" }}
        >
          <ZoomIn size={14} />
        </button>
      </div>
    </div>
  );
}
